import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useAddPreset,
  useUpdatePreset,
} from '@restDataAccess/camera/cameraControlApi.dataHooks.ts';
import { Button, FlexAligner, ModalContent, ModalFooter, ModalHeader } from '@scaleaq/scaleui';
import {
  AddOrUpdatePresetRequest,
  CameraInfo,
  InventoryType,
  PresetInfo,
} from '@scaleaq/sitecontrol-lib';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ControlledSelectField,
  ControlledTextOrNumberField,
  FeedbackAlert,
  FeedbackAlertProps,
} from '@/components';
import { useInventoryDataCtx } from '@/providers';
import { StyledModal } from '@/styles.ts';
import { ModalControllerType } from '@/types.ts';
import { Icon, responsive } from '@/utils';

import { PresetGroupSelectionFiled } from './addOrEditPresetModal/PresetGroupSelectionFiled.tsx';
import { PresetPositionFields } from './addOrEditPresetModal/PresetPositionFields.tsx';
import { presetModalTokens } from './presetModal.tokens.ts';

export const AddOrEditPresetModal = ({
  camera,
  modalController,
  onDefineNew,
  onSaved,
  selectedPreset,
}: {
  camera: CameraInfo;
  modalController: ModalControllerType;
  onDefineNew: () => void;
  onSaved: (saved: { name: string; operation: 'added' | 'updated' }) => void;
  selectedPreset?: PresetInfo;
}) => {
  const hasSmartWinch = camera.winchInfo?.winchType === InventoryType.SmartWinch;
  const [includeOrientation, setIncludeOrientation] = useState(!hasSmartWinch);
  const { t } = useTranslation(['cameraControl', 'common']);

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AddOrUpdatePresetRequest>({
    defaultValues: {
      name: '',
      levelPath: '',
      presetGroupIds: [],
      newPresetGroupNames: [],
    },
    mode: 'all',
  });
  const [feedback, setFeedback] = useState<FeedbackAlertProps>();
  const { mutate: addMutate, isPending: isAdding } = useAddPreset();
  const { mutate: updateMutate, isPending: isUpdating } = useUpdatePreset({
    presetId: selectedPreset?.id ?? '',
  });

  useEffect(() => {
    if (!modalController.open) {
      return;
    }

    const hasSavedOrientation = [selectedPreset?.pan, selectedPreset?.tilt].some(
      value => typeof value === 'number' && !Number.isNaN(value)
    );
    setIncludeOrientation(selectedPreset ? hasSavedOrientation : !hasSmartWinch);
    setFeedback(undefined);

    if (!selectedPreset) {
      reset();
      return;
    }

    const { id: _id, presetGroups, ...values } = selectedPreset;
    reset(
      {
        ...values,
        presetGroupIds: presetGroups?.map(group => group.id) ?? [],
        newPresetGroupNames: [],
      },
      { keepDefaultValues: true }
    );
  }, [hasSmartWinch, modalController.open, reset, selectedPreset]);

  const { inventoryById } = useInventoryDataCtx();
  const inventoryPathOptions = useMemo(() => {
    const cameraInventoryPath = camera.path;
    if (!cameraInventoryPath) {
      return [];
    }
    const pathSegments = cameraInventoryPath.split('.');
    return pathSegments.flatMap((segment, index) => {
      const inventoryItem = inventoryById.get(segment);
      if (
        inventoryItem?.type === InventoryType.Company ||
        inventoryItem?.type === InventoryType.Site
      ) {
        return [];
      }
      const value = pathSegments.slice(0, index + 1).join('.');
      return [
        {
          id: segment,
          value,
          label: inventoryItem?.name || segment,
          secondaryLabel: inventoryItem?.type || '',
        },
      ];
    });
  }, [inventoryById, camera.path]);

  const onSaveHandler = useCallback(
    (data: AddOrUpdatePresetRequest) => {
      const mutate = selectedPreset ? updateMutate : addMutate;
      mutate(data, {
        onSuccess: () => {
          modalController.onClose();
          onSaved({
            name: data.name,
            operation: selectedPreset ? 'updated' : 'added',
          });
        },
        onError: error => {
          const message = error instanceof Error ? error.message : '';
          setFeedback({
            text: t(selectedPreset ? 'common:snackbar.error.update' : 'common:snackbar.error.add', {
              name: data.name,
              message,
            }),
            variant: 'alarm',
          });
        },
      });
    },
    [addMutate, modalController, onSaved, selectedPreset, t, updateMutate]
  );

  return (
    <>
      <Button
        label={t('preset.defineNew')}
        headIcon={Icon.CirclePlus}
        variant={'ghost'}
        onClick={onDefineNew}
      />
      <StyledModal
        closeOnBackdropClick={true}
        closeOnEscapeClick={true}
        open={modalController.open}
        onClose={modalController.onClose}
        aria-label={t('preset.defineNew')}
        $width={presetModalTokens.addOrEdit.width}
        $maxWidth={presetModalTokens.addOrEdit.maxWidth}
        $minHeight={presetModalTokens.addOrEdit.minHeight}
      >
        <ModalHeader title={t('preset.defineNew')} onClose={modalController.onClose} />
        <ModalContent>
          <FeedbackAlert {...feedback} loading={isAdding || isUpdating} />
          <form onSubmit={handleSubmit(onSaveHandler)}>
            <FlexAligner direction='column' gap={responsive.spacing['3xlarge']}>
              <ControlledTextOrNumberField
                control={control}
                name={'name'}
                label={t('preset.name')}
                rules={{ required: t('common:nameRequired') }}
                critical={errors.name?.message}
              />
              <ControlledSelectField
                name={'levelPath'}
                control={control}
                label={t('preset.level')}
                placeholder={t('preset.levelPlaceholder')}
                options={inventoryPathOptions}
                rules={{ required: t('common:cannotBeEmpty') }}
                critical={errors.levelPath?.message}
              />
              <PresetPositionFields
                camera={camera}
                control={control}
                hasSmartWinch={hasSmartWinch}
                includeOrientation={includeOrientation}
                onIncludeOrientationChange={setIncludeOrientation}
                setValue={setValue}
              />
              <PresetGroupSelectionFiled
                key={`${modalController.open}-${selectedPreset?.id ?? 'new'}`}
                control={control}
                setValue={setValue}
              />
            </FlexAligner>
          </form>
        </ModalContent>
        <ModalFooter
          actionButtons={[
            {
              label: t('common:save'),
              onClick: handleSubmit(onSaveHandler),
            },
            { label: t('common:close'), onClick: modalController.onClose },
          ]}
        />
      </StyledModal>
    </>
  );
};
